/**
 * Connector UI state (F-38) — one store per open board view, owned by
 * BoardCanvas. LOCAL view state only; the connectors themselves live in the
 * Yjs doc (`@kola/shared` `getConnectors`).
 *
 * Three things are shared here because two different layers need them and
 * neither owns the other:
 *
 *  - `draft` — the line being dragged out of a port. ElementsLayer STARTS it
 *    (the ports are drawn on the elements) and the ConnectorsLayer both draws
 *    it and drives it to completion, since a drag can end anywhere on the
 *    canvas, not just over an element.
 *  - `hover` — the port the cursor is currently "around" (F-38: the start point
 *    shows when the cursor is near an edge). Kept here rather than in the
 *    hovered element so that only ONE port is ever armed board-wide, however
 *    the elements overlap.
 *  - `selectedId` — the selected line. Separate from the element selection: a
 *    connector is not an element, and Del must resolve to exactly one of them
 *    (BoardCanvas clears the other whenever one is set).
 */
import type { ConnectorEndpoint, ConnectorSide } from "@kola/shared";
import { createStore, type StoreApi } from "zustand/vanilla";

import type { Point, PortCandidate } from "./geometry.js";

/**
 * How close (SCREEN px, so the affordance feels the same at any zoom) the
 * cursor must come to an edge for that side's port to arm.
 */
export const PORT_HOVER_SCREEN_PX = 14;

/**
 * How close (SCREEN px) a dragged end must come to a candidate port to SNAP to
 * it. Wider than the hover band: while dragging, the user is aiming at a target
 * they can see highlighted, and a generous catch radius is what makes the
 * gesture feel like it clicks into place.
 */
export const PORT_SNAP_SCREEN_PX = 32;

/** A connector being dragged out of a port, before it is committed. */
export interface ConnectorDraft {
  /**
   * The pointer that owns this drag. Checked by the window listeners that
   * drive it: on a touch screen a second finger's move/up would otherwise
   * steer and then commit a line the first finger is still drawing.
   */
  pointerId: number;
  /** The port the drag started from — the line's `from` end. */
  from: ConnectorEndpoint;
  /** Live pointer position, in board units. */
  pointer: Point;
  /**
   * The port this would attach to on release — the snap preview. Null means the
   * pointer is over open canvas and releasing creates nothing.
   */
  snap: PortCandidate | null;
}

export interface ConnectorUiState {
  draft: ConnectorDraft | null;
  /** The port armed under the cursor (F-38 "starting point shows"). */
  hover: { elementId: string; side: ConnectorSide } | null;
  selectedId: string | null;

  beginDraft(from: ConnectorEndpoint, pointer: Point, pointerId: number): void;
  updateDraft(pointer: Point, snap: PortCandidate | null): void;
  endDraft(): void;
  setHover(hover: { elementId: string; side: ConnectorSide } | null): void;
  setSelected(connectorId: string | null): void;
}

export type ConnectorUiStore = StoreApi<ConnectorUiState>;

export function createConnectorUiStore(): ConnectorUiStore {
  return createStore<ConnectorUiState>((set, get) => ({
    draft: null,
    hover: null,
    selectedId: null,
    beginDraft: (from, pointer, pointerId) =>
      set({ draft: { pointerId, from, pointer, snap: null }, selectedId: null }),
    updateDraft: (pointer, snap) => {
      const draft = get().draft;
      if (draft === null) return; // the drag was cancelled between frames
      set({ draft: { pointerId: draft.pointerId, from: draft.from, pointer, snap } });
    },
    endDraft: () => {
      if (get().draft !== null) set({ draft: null });
    },
    setHover: (hover) => {
      const current = get().hover;
      // Pointer moves fire per frame; only a CHANGE of armed port is a render.
      if (current?.elementId === hover?.elementId && current?.side === hover?.side) return;
      set({ hover });
    },
    setSelected: (connectorId) => {
      if (get().selectedId !== connectorId) set({ selectedId: connectorId });
    },
  }));
}
